/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  InputNumber,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';
import { getPeriodosVacacionales } from 'api/catalogos/PeriodosVacacionales';
import {
  calcularDiasHabiles,
  ModelInfoConfigGuardVacaPeriodos,
} from 'api/configuraciones/ConfigurarGuardiasVacacionales/ConfigurarGuardiasVacacionalesPeriodos';
import { ModelInfoConfigGuardVacaExcepcion } from 'api/configuraciones/ConfigurarGuardiasVacacionales/ConfigurarGuardiasVacacionalesExcepciones';
import moment from 'moment';

const modelPermissionName = 'configurarguardiasvacacionales';

export const configurarGuardiasVacacionalesPeriodosPermission = {
  permissionModel: modelPermissionName,
  catalogsModels: [],
  tabsModels: [],
};

const ConfigurarGuardiasVacacionalesPeriodos = ({ permission, idFather }) => {
  const [form] = Form.useForm();
  const fechaInicio = Form.useWatch('fecha_inicial', form);
  const fechaFin = Form.useWatch('fecha_final', form);
  const formInstances = [
    form,
  ];

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [PeriodosVacacionales, setPeriodosVacacionales] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [open, setVisible] = useState(false);
  const [estados, setEstados] = useState([]);
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);
  const [disableFields, setDisableFields] = useState(false);

  const registroName = `${selectedData?.periodos_vacacionales?.periodo}`;
  const {
    cliente,
    nameModel,
    nameModelSingular,
    formatter,
  } = ModelInfoConfigGuardVacaPeriodos;

  const fetchData = async () => {
    setLoading(true);
    const _data = await cliente.obtenerListaDeRegistros(
      {
        configurar_guardias_vacacionales_general: idFather[0],
      },
    );
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _periodosVacacionales,
        _estados,
        _data,
      ] = await Promise.all([
        getPeriodosVacacionales({ estados_globales: ESTATUS_AUTORIZADO }),
        getEstadosGlobales(),
        cliente.obtenerListaDeRegistros(
          { configurar_guardias_vacacionales_general: idFather[0] },
        ),
      ]);
      if (mounted) {
        setPeriodosVacacionales(_periodosVacacionales);
        setEstados(_estados);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!fechaInicio || !fechaFin) {
      form.setFieldsValue({ no_dias: 0 });
      return;
    }

    const dias = calcularDiasHabiles(fechaInicio, fechaFin);
    form.setFieldsValue({ no_dias: dias });
  }, [fechaInicio, fechaFin]);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });

    const dataForForm = formatter.formatRecordForRead(record);

    form.setFieldsValue(dataForForm);
  };
  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const valuesForm = form.getFieldsValue();
      const [key] = selectedRowKeys;

      const values = formatter.formatRecordForSend(valuesForm);
      const saved = await (
        key
          ? cliente.actualizarRegistro(key, values)
          : cliente.crearRegistro(values)
      );
      if (saved) {
        if (_continue) {
          handleOnRowClick(saved);
        } else {
          onCancel();
        }
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };
  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data?.find((item) => item.id === key);
    const _regristo = await cliente.obtenerUnRegistro(match.id);

    setTimeout(() => {
      if (_regristo) {
        const formValues = formatter.formatRecordForRead(_regristo);
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(formValues));
          setDisableFields(_regristo?.estados_globales?.id !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };
  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await cliente.borrarRegistro(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, `No se puede autorizar ${nameModelSingular} ya autorizado`);
        return;
      }
      const saved = await cliente.autorizarRegistro(key);
      if (saved) {
        await fetchData();
        onCancel();
      }
      onCancel();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        return;
      }
      const saved = await cliente.actualizarRegistro(key, { estados_globales: ESTATUS_CANCELADO });
      if (saved) {
        await fetchData();
      }
      onCancel();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmReactivar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        return;
      }
      const saved = await cliente.actualizarRegistro(key, { estados_globales: ESTATUS_ACTIVO });
      if (saved) {
        await fetchData();
      }
      onCancel();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
    },
    {
      titleText: 'Periodo Vacacional',
      dataIndex: 'periodos_vacacionales',
      key: 'periodos_vacacionales',
      width: 200,
      render: (_, record) => record?.periodos_vacacionales?.periodo,
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 150,
      render: (val) => moment(val).format('DD/MM/YYYY'),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 150,
      render: (val) => moment(val).format('DD/MM/YYYY'),
    },
    {
      titleText: 'No. de Días',
      dataIndex: 'no_dias',
      key: 'no_dias',
      width: 150,
    },
    {
      titleText: 'Fecha de Inicio de Registro',
      dataIndex: 'fecha_inicio_registro',
      key: 'fecha_inicio_registro',
      width: 200,
      render: (val) => moment(val).format('DD/MM/YYYY'),
    },
    {
      titleText: 'Fecha de Final de Registro',
      dataIndex: 'fecha_final_registro',
      key: 'fecha_final_registro',
      width: 200,
      render: (val) => moment(val).format('DD/MM/YYYY'),
    },
    {
      titleText: 'Fecha de Cierre',
      dataIndex: 'fecha_de_cierre',
      key: 'fecha_de_cierre',
      width: 180,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estado',
      key: 'estado',
      width: 80,
      render: (_, record) => record?.estados_globales?.descripcion || '',
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          count={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: modelPermissionName, action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales?.id !== ESTATUS_ACTIVO,
              className: 'success',
            },
            {
              onClick: () => setVisibleCancelar(true),
              permission: { model: modelPermissionName, action: 'cancelar' },
              icon: <CloseCircleOutlined />,
              text: 'Cancelar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales?.id !== ESTATUS_AUTORIZADO,
              className: 'danger',
            },
            {
              onClick: () => setVisibleReactivar(true),
              permission: { model: modelPermissionName, action: 'reactivar' },
              icon: <RedoOutlined />,
              text: 'Reactivar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales?.id !== ESTATUS_CANCELADO,
              className: 'success',
            },
          ]}
          permission={permission}
          allowExportParam={false}
          disableEdit={selectedData?.estados_globales?.id !== ESTATUS_ACTIVO}
          disableDelete={selectedData?.estados_globales?.id !== ESTATUS_ACTIVO}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${nameModel} `}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstErrorDatePicker
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="clave"
                label="Clave"
                rules={rules.required}
                hasFeedback
              >
                <InputNumber
                  allowClear
                  style={{ width: '100%' }}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="periodos_vacacionales"
                label="Periodo Vacacional"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="periodo"
                  keyProp="id"
                  dataSource={PeriodosVacacionales}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_inicial"
                label="Fecha Inicial"
                rules={rules.required}
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="DD/MM/YYYY"
                  placeholder=""
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_final"
                label="Fecha Final"
                rules={rules.required}
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="DD/MM/YYYY"
                  placeholder=""
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="no_dias"
                label="No. de Días"
                rules={rules.required}
                hasFeedback
              >
                <InputNumber disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_inicio_registro"
                label="Fecha de Inicio de Registro"
                rules={rules.required}
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="DD/MM/YYYY"
                  placeholder=""
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_final_registro"
                label="Fecha de Final de Registro"
                rules={rules.required}
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="DD/MM/YYYY"
                  placeholder=""
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_de_cierre"
                label="Fecha de Cierre"
                rules={rules.required}
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="DD/MM/YYYY"
                  placeholder=""
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_inicial_vigencia_vacaciones"
                label="Fecha Inicial de Vigencia de Vacaciones"
                rules={rules.required}
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="DD/MM/YYYY"
                  placeholder=""
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_final_vigencia_vacaciones"
                label="Fecha Final de Vigencia de Vacaciones"
                rules={rules.required}
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="DD/MM/YYYY"
                  placeholder=""
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="configurar_guardias_vacacionales_general"
                label="FatherId"
                hasFeedback
                hidden
                initialValue={idFather[0]}
              >
                <Input
                  hidden
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`${nameModelSingular} ${registroName}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar ${nameModelSingular} ${registroName}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          ConfirmCancel
          onCancel={() => setVisibleCancelar(false)}
          contentTitle={`Cancelar ${nameModelSingular} ${registroName}`}
          open={visibleCancelar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle={`Reactivar ${nameModelSingular} ${registroName}`}
          open={visibleReactivar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar ${nameModelSingular} ${registroName}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ConfigurarGuardiasVacacionalesPeriodos;
